import { defineStore } from 'pinia'
import { useStorage } from '@vueuse/core'
import { supabase } from '@/core/utils/supabase'
import { type User, type Session, AuthError } from '@supabase/supabase-js'
import { createDebugger } from '@/core/utils/debug'
import { VITE_APP_URL } from '@/core/constants/env'
const authDebugger = createDebugger('AuthStore')

export const useAuthStore = defineStore('auth', {
  state: () => ({
    authUser: null as User | null,
    session: null as Session | null,
    loading: false,
    error: null as AuthError | null,
    isNewUser: useStorage<boolean>('isNewUser', false),
  }),

  getters: {
    user: state => state.authUser,
    isAuthenticated: state => !!state.authUser,
    getIsNewUser: state => state.isNewUser,
  },

  actions: {
    /**
     * Register user only with Supabase (without tenant registration)
     * This method is used in the new registration flow
     */
    async registerUserOnly(email: string, password: string, options?: { data?: { [key: string]: any; }; emailRedirectTo?: string; }) {
      try {
        this.loading = true
        this.error = null

        const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options,
        })

        if (signUpError) {
          this.error = signUpError
          return { user: null, session: null, error: signUpError }
        }

        if (signUpData.user) {
          this.authUser = signUpData.user
          this.session = signUpData.session
          this.isNewUser = true // Mark as new user for registration flow

          authDebugger.info('User registered successfully with Supabase')
          return { user: signUpData.user, session: signUpData.session, error: null }
        }

        return { user: null, session: null, error: null }
      } catch (error) {
        this.error = error as AuthError
        return { user: null, session: null, error }
      } finally {
        this.loading = false
      }
    },

    async register(email: string, password: string, options?: { data?: { name?: string }, emailRedirectTo?: string }) {
      try {
        this.loading = true
        this.error = null

        const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options,
        })

        if (signUpError) {
          this.error = signUpError
          return { user: null, session: null, error: signUpError }
        }

        if (signUpData.user) {
          this.authUser = signUpData.user
          this.session = signUpData.session
          this.isNewUser = true // Mark as new user for registration flow

          authDebugger.info('User registered successfully with Supabase')
          return { user: signUpData.user, session: signUpData.session, error: null }
        }

        return { user: null, session: null, error: null }
      } catch (error) {
        this.error = error as AuthError
        return { user: null, session: null, error }
      } finally {
        this.loading = false
      }
    },

    async logIn(email: string, password: string) {
      try {
        this.loading = true
        this.error = null

        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        })

        if (error) {
          this.error = error
          return { user: null, session: null, error }
        }

        this.authUser = data.user
        this.session = data.session

        authDebugger.info('User logged in successfully')
        return { user: data.user, session: data.session, error: null }
      } catch (error) {
        this.error = error as AuthError
        return { user: null, session: null, error }
      } finally {
        this.loading = false
      }
    },

    async socialLogin(provider: string, redirectTo?: string) {
      try {
        this.loading = true
        this.error = null

        const { data, error } = await supabase.auth.signInWithOAuth({
          provider: provider as any,
          options: {
            redirectTo: redirectTo || `${VITE_APP_URL}/auth/callback`,
          },
        })

        if (error) {
          this.error = error
          console.error(`${provider} login error:`, error)
          return { data: null, error }
        }

        authDebugger.info(`${provider} login initiated, redirecting to OAuth provider...`)
        return { data, error: null }
      } catch (error) {
        this.error = error as AuthError
        console.error('Social login error:', error)
        return { data: null, error }
      } finally {
        this.loading = false
      }
    },

    /**
     * Update user password
     */
    async updatePassword(newPassword: string) {
      try {
        this.loading = true
        this.error = null

        const { data, error } = await supabase.auth.updateUser({
          password: newPassword
        })

        if (error) {
          this.error = error
          return { user: null, error }
        }

        authDebugger.info('Password updated successfully')
        return { user: data.user, error: null }
      } catch (error) {
        this.error = error as AuthError
        console.error('Update password error:', error)
        return { user: null, error }
      } finally {
        this.loading = false
      }
    },

    /**
     * Send password reset email
     */
    async forgotPassword(email: string) {
      try {
        this.loading = true
        this.error = null

        const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${VITE_APP_URL}/reset-password`,
        })

        if (error) {
          this.error = error
          return { data: null, error }
        }

        authDebugger.info('Password reset email sent successfully')
        return { data, error: null }
      } catch (error) {
        this.error = error as AuthError
        console.error('Forgot password error:', error)
        return { data: null, error }
      } finally {
        this.loading = false
      }
    },

    /**
     * Update password with access token (from reset email)
     */
    async resetPassword(newPassword: string) {
      try {
        this.loading = true
        this.error = null

        const { data, error } = await supabase.auth.updateUser({
          password: newPassword
        })

        if (error) {
          this.error = error
          return { user: null, error }
        }

        authDebugger.info('Password reset successfully')
        return { user: data.user, error: null }
      } catch (error) {
        this.error = error as AuthError
        console.error('Reset password error:', error)
        return { user: null, error }
      } finally {
        this.loading = false
      }
    },

    async logout() {
      try {
        this.loading = true
        
        const { data: sessionData } = await supabase.auth.getSession()
        
        if (sessionData.session) {
          const { error } = await supabase.auth.signOut()
          
          if (error) {
            console.warn('Logout error (continuing with cleanup):', error)
            this.error = error
            
            const isTokenError = error.message?.toLowerCase().includes('forbidden') ||
                               error.message?.toLowerCase().includes('invalid') ||
                               error.message?.toLowerCase().includes('expired')
            
            if (!isTokenError) {
              return { error }
            }
          }
        } else {
          authDebugger.info('No active session found, proceeding with local cleanup')
        }

        // Clear all application data
        this.clearAllAppData()

        authDebugger.info('Logout completed successfully - all data cleared')
        return { error: null }
      } catch (error) {
        console.error('Logout error:', error)
        
        // Force cleanup even on error
        this.clearAllAppData()
        this.error = error as AuthError
        
        return { error: error as AuthError }
      } finally {
        this.loading = false
      }
    },

    async checkSession() {
      const { data, error } = await supabase.auth.getSession()

      if (error) {
        this.error = error
        return { session: null, error }
      }

      this.session = data.session
      if (data.session?.user) {
        this.authUser = data.session.user
      } else {
        // No session found, clear all user data and cache
        this.authUser = null
      }

      return { session: data.session, error: null }
    },

    async refreshSession() {
      try {
        this.loading = true
        this.error = null

        const { data, error } = await supabase.auth.refreshSession()

        if (error) {
          this.error = error
          console.error('Failed to refresh session:', error)
          return { session: null, error }
        }

        this.session = data.session
        if (data.session?.user) {
          this.authUser = data.session.user
          authDebugger.info('Session refreshed successfully')
        } else {
          this.authUser = null
          console.warn('Session refresh returned no session')
        }

        return { session: data.session, error: null }
      } catch (error) {
        this.error = error as AuthError
        console.error('Error during session refresh:', error)
        return { session: null, error }
      } finally {
        this.loading = false
      }
    },

    // Utility function to reset all stores
    clearAllAppData() {
      try {
        // Clear all service stores data
        try {
          // Dynamic imports to avoid circular dependency
          Promise.all([
            import('@/modules/services/BackendService/ChatbotService').then(({ useChatbotStore }) => {
              const chatbotStore = useChatbotStore()
              chatbotStore.clearAllData()
            }),
            import('@/modules/services/BackendService/DataSourceService').then(({ useDataSourceStore }) => {
              const dataSourceStore = useDataSourceStore()
              dataSourceStore.clearAllData()
            }),
            import('@/modules/services/BackendService/ChatboxService').then(({ useChatboxConfigStore }) => {
              const chatboxStore = useChatboxConfigStore()
              chatboxStore.clearAllData()
            }),
            import('@/modules/services/BackendService/TenantService').then(({ useTenantStore }) => {
              const tenantStore = useTenantStore()
              tenantStore.resetTenantState()
            })
          ]).catch(error => {
            console.warn('Could not clear one or more service stores:', error)
          })
        } catch (error) {
          console.warn('Could not clear service stores:', error)
        }
        
        // Reset auth state
        this.authUser = null
        this.session = null
        this.error = null
        this.isNewUser = false
        
        // Clear localStorage
        localStorage.removeItem('authUser')
        localStorage.removeItem('isNewUser')
        
        // Clear Supabase localStorage
        const supabaseKeys = Object.keys(localStorage).filter(key => 
          key.startsWith('sb-') || key.includes('supabase')
        )
        supabaseKeys.forEach(key => localStorage.removeItem(key))
        
        authDebugger.info('All application data cleared')
      } catch (error) {
        console.error('Error clearing application data:', error)
      }
    },

    initAuthListener() {
      supabase.auth.onAuthStateChange((event, session) => {
        authDebugger.debug('Auth state changed:', event)

        if (event === 'SIGNED_IN' && session) {
          this.session = session
          this.authUser = session.user
        }

        if (event === 'SIGNED_OUT') {
          this.session = null
          this.authUser = null
          this.isNewUser = false
        }

        if (event === 'USER_UPDATED' && session) {
          this.session = session
          this.authUser = session.user
        }

        if (event === 'TOKEN_REFRESHED' && session) {
          authDebugger.debug('Token was refreshed automatically')
          this.session = session
          this.authUser = session.user
        }

        // Handle cases where the refresh token itself expires
        if (event === 'INITIAL_SESSION' && !session) {
          // No valid session found on initial load
          this.authUser = null
          this.session = null
          this.isNewUser = false
        }
      })

      // Setup periodic session validation
      const sessionCheckInterval = 10 * 60 * 1000 // Check every 10 minutes
      setInterval(async () => {
        const { data } = await supabase.auth.getSession()
        if (!data.session) return

        // If session expires soon (within next 15 minutes), refresh it proactively
        const expiresAt = data.session.expires_at
        if (expiresAt) {
          const expiresIn = expiresAt - Math.floor(Date.now() / 1000)
          const fifteenMinutes = 15 * 60 // 15 minutes in seconds

          if (expiresIn < fifteenMinutes) {
            authDebugger.info('Token expires soon, refreshing proactively')
            await this.refreshSession()
          }
        }
      }, sessionCheckInterval)
    },

    // Set new user flag
    setIsNewUser(isNew: boolean) {
      this.isNewUser = isNew
    }
  },
})
